/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 */
package studio

import studio.data.tax.Brackets2025

/**
 * Combined federal + provincial marginal-rate calculator.
 *
 * Returns the rate that applies to the *next dollar* of income — not the
 * average rate. For provinces with surtaxes (currently Ontario), the surtax
 * is folded into the effective marginal at incomes where it would actually
 * apply.
 *
 * Per §4.1 of the spec, this is a pure module — no I/O, no platform calls.
 */
object MarginalRateCalculator {

    /**
     * @param income taxable income in CAD (gross is close enough for v1)
     * @param province the user's province
     * @param taxYear which year's bracket tables to use
     * @return combined fed + provincial marginal rate (0..1)
     */
    fun marginalRate(income: Double, province: ProvinceCode, taxYear: Int): Double {
        val federal = bracketRate(income, federalTable(taxYear))
        val provincial = provincialEffectiveRate(income, province, taxYear)
        return federal + provincial
    }

    /**
     * Provincial component — includes surtax bump for ON. Returns the rate
     * applied to the next provincial dollar; the federal portion is added in
     * [marginalRate].
     */
    fun provincialEffectiveRate(income: Double, province: ProvinceCode, taxYear: Int): Double {
        val base = bracketRate(income, Brackets2025.provincial(province))
        return when (province) {
            ProvinceCode.ON -> base + ontarioSurtaxMarginalBump(income, base)
            else -> base
        }
    }

    /**
     * Ontario's surtax is applied to *provincial tax*, not income. The
     * marginal bump on the next dollar of income equals (next dollar's
     * provincial tax) × (surtax rate). At incomes where provincial tax has
     * already exceeded the 36% threshold, both surtaxes stack.
     */
    private fun ontarioSurtaxMarginalBump(income: Double, baseProvincialRate: Double): Double {
        val provincialTax = totalProvincialTax(income, ProvinceCode.ON)
        var bump = 0.0
        if (provincialTax > Brackets2025.ONTARIO_SURTAX_THRESHOLD_20PCT) {
            bump += baseProvincialRate * Brackets2025.ONTARIO_SURTAX_RATE_LOW
        }
        if (provincialTax > Brackets2025.ONTARIO_SURTAX_THRESHOLD_36PCT) {
            bump += baseProvincialRate * Brackets2025.ONTARIO_SURTAX_RATE_HIGH
        }
        return bump
    }

    /**
     * Sum provincial tax across all brackets up to [income]. Used by the
     * Ontario surtax logic; not exposed as a top-level "total tax" function
     * because that's a different need (effective vs marginal) and would
     * encourage callers to confuse the two.
     */
    private fun totalProvincialTax(income: Double, province: ProvinceCode): Double {
        var tax = 0.0
        var remaining = income
        for (bracket in Brackets2025.provincial(province).brackets) {
            if (remaining <= 0) break
            val upper = bracket.upperBound ?: Double.MAX_VALUE
            val taxableInBand = minOf(remaining, upper - bracket.lowerBound)
                .coerceAtLeast(0.0)
            tax += taxableInBand * bracket.rate
            remaining -= taxableInBand
            if (bracket.upperBound == null) break
        }
        return tax
    }

    /** Find which bracket [income] falls in and return that bracket's rate. */
    private fun bracketRate(income: Double, table: TaxTable): Double {
        for (bracket in table.brackets) {
            val upper = bracket.upperBound ?: Double.MAX_VALUE
            if (income > bracket.lowerBound && income <= upper) return bracket.rate
        }
        // Income at exactly 0: lowest bracket
        return table.brackets.firstOrNull()?.rate ?: 0.0
    }

    private fun federalTable(@Suppress("UNUSED_PARAMETER") taxYear: Int): TaxTable =
        Brackets2025.FEDERAL  // PR1 only ships 2025; year selection is a future hook
}
